/**
 * 폴더 CRUD API.
 * 컴포넌트마다 흩어져 있던 raw fetch + 수동 토큰 헤더를 공용 클라이언트로 모은다.
 * 공유·권한 관련은 folderService.ts(FolderService)가 담당한다.
 *
 * 폴더 라우트는 data 봉투 없이 최상위 키로 응답한다 (예: { success, folders }).
 */

import { apiService } from './api'

export interface FolderSummary {
  _id: string
  name: string
  description?: string
  color: string
  icon: string
  isDefault?: boolean
  parentFolder?: string | FolderSummary | null
  teamId?: string | null
  isShared?: boolean
  permissions?: { canEdit: string[]; canView: string[] }
  urlCount?: number
  children?: FolderSummary[]
}

export interface FolderListResult {
  folders: FolderSummary[]
  totalLinks: number
  favoriteLinks: number
}

export interface CreateFolderInput {
  name: string
  description?: string
  color?: string
  icon?: string
  parentFolder?: string | null
}

export type UpdateFolderInput = Partial<CreateFolderInput>

interface FoldersResponse {
  success: boolean
  folders?: FolderSummary[]
  totalLinks?: number
  favoriteLinks?: number
  message?: string
}

interface FolderResponse {
  success: boolean
  folder?: FolderSummary
  message?: string
}

export const folderApi = {
  /** 폴더 목록 + 전체/즐겨찾기 링크 수 */
  async listWithCounts(): Promise<FolderListResult> {
    const response = await apiService.client.get<FoldersResponse>('/folders')
    return {
      folders: response.data.folders ?? [],
      totalLinks: response.data.totalLinks ?? 0,
      favoriteLinks: response.data.favoriteLinks ?? 0,
    }
  },

  /** 폴더 목록만 필요할 때 */
  async list(): Promise<FolderSummary[]> {
    const result = await this.listWithCounts()
    return result.folders
  },

  async create(input: CreateFolderInput): Promise<FolderSummary | null> {
    const response = await apiService.client.post<FolderResponse>(
      '/folders',
      input
    )
    return response.data.folder ?? null
  },

  async update(
    folderId: string,
    input: UpdateFolderInput
  ): Promise<FolderSummary | null> {
    const response = await apiService.client.put<FolderResponse>(
      `/folders/${folderId}`,
      input
    )
    return response.data.folder ?? null
  },

  async remove(folderId: string): Promise<void> {
    await apiService.client.delete(`/folders/${folderId}`)
  },
}

export default folderApi
